import { Location } from '@angular/common';
import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { PARTNER_DETAIL_TABS_VIEW } from '../../constants/company-detail-tabs.constant';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import {
  CompanyDetailTab,
  PartnerModel,
  RoleModel,
  PartnerUsersResponseModel,
  getUserActivePartner,
} from '@core/models';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CompanyStore } from '@root/app/store/company.store';
import { UserDialogComponent } from '../../components';
import { CompanyGeneralTabComponent } from '../../components/company-general-tab/company-general-tab.component';
import { CompanyUsersTabComponent } from '../../components/company-users-tab/company-users-tab.component';
import { TabBarComponent, PageBodyComponent } from '@custom-components';
import { MatDialog } from '@angular/material/dialog';
import { UserStore } from '@root/app/store/user.store';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AuthStore } from '@root/app/store/auth.store';
import { DeleteUserDialogComponent } from '../../components/delete-user-dialog/delete-user-dialog.component';
import { ResendInviteDialogComponent } from '../../components/resend-invite-dialog/resend-invite-dialog.component';
import { RecentlyVisitedService } from '@core/services';

@Component({
  selector: 'xemex-company-detail',
  imports: [
    MatButtonModule,
    RouterModule,
    TabBarComponent,
    PageBodyComponent,
    CompanyGeneralTabComponent,
    CompanyUsersTabComponent,
    TranslateModule,
    MatSidenavModule,
    PageHeaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './company-detail.component.html',
})
export class PartnerDetailComponent {
  readonly companyStore = inject(CompanyStore);
  readonly userStore = inject(UserStore);
  readonly authStore = inject(AuthStore);
  private readonly translate = inject(TranslateService);
  private readonly recentlyVisited = inject(RecentlyVisitedService);
  private readonly route = inject(ActivatedRoute);
  private readonly routerLocation = inject(Location);
  private readonly dialog = inject(MatDialog);
  private recentlyVisitedRecordedPartnerOid: string | undefined;
  companyDetail: PartnerModel | undefined;
  roles: RoleModel[] = [];
  users: PartnerUsersResponseModel = { numberOfItems: 0, users: [] };

  APP_PATHS = APP_PATHS;
  PARTNER_DETAIL_TABS_VIEW = PARTNER_DETAIL_TABS_VIEW;
  CompanyDetailTab = CompanyDetailTab;
  partnerOid = '';
  selectedTab = 'general';

  /** True when the current user is a Xemex user (active partner is the Xemex OID). */
  get isXemex(): boolean {
    return getUserActivePartner(this.userStore.user())?.partnerOid === XEMEX_OID;
  }

  /** True when the viewed partner is the current user's own active partner. */
  get isOwnPartner(): boolean {
    const activePartnerOid = getUserActivePartner(this.userStore.user())?.partnerOid;
    return !!activePartnerOid && activePartnerOid === this.companyStore.companyDetail()?.partnerOid;
  }

  get breadcrumbs(): BreadcrumbItem[] {
    return [
      { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
      {
        label: this.translate.instant('sidebarMenu.partners'),
        routerLink: ['/', APP_PATHS.Partners],
      },
      { label: this.companyDetail?.partnerName ?? '—' },
    ];
  }

  // Track which tabs have loaded their data to avoid duplicate fetches
  private loadedTabs = {
    general: false,
    users: false,
  };

  constructor() {
    this.route.params.subscribe(params => {
      if (params['partnerOid']) {
        this.partnerOid = params['partnerOid'];
        // Only fetch general tab data and company details on initialization
        this.companyStore.getCompanyDetailMethod(this.partnerOid);
        this.loadedTabs.general = true;
      }

      // Load the active tab's data (defaults to general). loadTabData is
      // idempotent via loadedTabs, so repeat param emissions don't re-fetch.
      this.selectedTab = params['tab'] ?? 'general';
      this.loadTabData(this.selectedTab);
    });

    effect(() => {
      this.companyDetail = this.companyStore.companyDetail();
      this.users = this.companyStore.usersByPartnerId();
      if (
        this.companyDetail &&
        this.recentlyVisitedRecordedPartnerOid !== this.companyDetail.partnerOid
      ) {
        this.recentlyVisitedRecordedPartnerOid = this.companyDetail.partnerOid;
        this.recentlyVisited.record({
          type: 'partner',
          id: this.companyDetail.partnerOid,
          icon: 'domain',
          primary: this.companyDetail.partnerName,
          secondary: this.translate.instant('recentlyUsed.types.partner'),
          route: ['/', APP_PATHS.Partners, this.companyDetail.partnerOid],
        });
      }
    });

    effect(() => {
      if (this.userStore.roleData()) {
        this.roles = this.userStore.roleData().roles;
      }
    });
  }

  private loadTabData(tab: string) {
    if (this.loadedTabs[tab as keyof typeof this.loadedTabs]) {
      return; // Already loaded
    }

    switch (tab) {
      case 'users':
        this.userStore.getRolesMethod();
        this.companyStore.getUsers({ partnerOid: this.partnerOid });
        break;
      case 'general':
        // General tab data is already loaded in constructor
        break;
    }

    this.loadedTabs[tab as keyof typeof this.loadedTabs] = true;
  }

  onClickBack() {
    this.routerLocation.back();
  }

  permissions(): string[] {
    const partnerOid = this.companyDetail?.partnerOid;
    const permissions = this.authStore.permissions();
    return (
      permissions
        ?.filter(p => p.groupObjectId == partnerOid || p.groupObjectId == XEMEX_OID)
        .flatMap(p => p.roles.flatMap(role => role.features.flatMap(f => f.features)))
        .filter(f => f.featureEnabled)
        .map(f => f.featureName) ?? []
    );
  }

  onCreateUserClicked() {
    this.dialog
      .open(UserDialogComponent, {
        width: '48rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        data: {
          mode: 'create',
          partnerOid: this.partnerOid,
          roles: this.roles,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.companyStore.getUsers({ partnerOid: this.partnerOid });
      });
  }

  onEditUserClicked(event: { userOid: string }) {
    const user = this.users.users.find(u => u.objectId === event.userOid);
    if (!user) {
      console.error('User not found:', event.userOid);
      return;
    }

    this.dialog
      .open(UserDialogComponent, {
        width: '48rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        data: {
          mode: 'edit',
          partnerOid: this.partnerOid,
          roles: this.roles,
          user: user,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.companyStore.getUsers({ partnerOid: this.partnerOid });
      });
  }

  onTabChange(tabRoute: string) {
    this.selectedTab = tabRoute;
    this.loadTabData(tabRoute);
  }

  onDeleteUser(event: { userOid: string }) {
    const user = this.users.users.find(u => u.objectId === event.userOid);
    if (!user) {
      return;
    }
    const currentUserOid = this.authStore.oid();

    this.dialog
      .open(DeleteUserDialogComponent, {
        width: '640px',
        maxWidth: '640px',
        height: '220px',
        data: {
          partnerOid: this.partnerOid,
          userOid: event.userOid,
          currentUserOid: currentUserOid,
        },
      })
      .afterClosed()
      .subscribe(() => {
        this.companyStore.getUsers({ partnerOid: this.partnerOid });
      });
  }
  onResendInvite(event: { userOid: string }) {
    this.dialog.open(ResendInviteDialogComponent, {
      width: '640px',
      maxWidth: '640px',
      height: '220px',
      data: {
        userOid: event.userOid,
      },
    });
  }

  onPageChange(event: { pageIndex: number; pageSize: number }) {
    // Fetch users for the current page (use page number instead of item offset)
    this.companyStore.getUsers({
      partnerOid: this.partnerOid,
      limit: event.pageSize,
      offset: event.pageIndex,
    });
  }
}
